import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { CopyField } from './CopyField'

interface TunnelPorts {
  ports: { port: number; name: string }[]
  downloads: string[]
}

interface TunnelOpened {
  id: string
  code: string
  port: number
  port_name: string
  device_name: string
  customer_name: string
  server: string
  expires_in_s: number
}

/** Anzeigenamen der Programm-Downloads. */
const PLATTFORMEN: Record<string, string> = {
  'netzwarte-tunnel-windows-amd64.exe': 'Windows',
  'netzwarte-tunnel-darwin-arm64': 'macOS (Apple Silicon)',
  'netzwarte-tunnel-darwin-amd64': 'macOS (Intel)',
  'netzwarte-tunnel-linux-amd64': 'Linux (x86-64)',
  'netzwarte-tunnel-linux-arm64': 'Linux (ARM64)',
}

/**
 * Port-Tunnel (Stufe 3.3): ein freigegebener Port des Geräts, etwa Winbox,
 * auf dem eigenen Rechner. Die Netzwarte zeigt den Tunnel-Code genau einmal;
 * das Programm netzwarte-tunnel fragt beim Start danach.
 */
export function PortTunnel({ deviceId, onClose }: { deviceId: string; onClose: () => void }) {
  const angebot = useQuery({
    queryKey: ['tunnel-ports', deviceId],
    queryFn: ({ signal }) => apiGet<TunnelPorts>(`/devices/${deviceId}/tunnel`, signal),
  })
  const [port, setPort] = useState<number | null>(null)
  const oeffnen = useMutation({
    mutationFn: (p: number) => apiSend<TunnelOpened>('POST', `/devices/${deviceId}/tunnel`, { port: p }),
  })

  const ports = angebot.data?.ports ?? []
  const gewaehlt = port ?? ports[0]?.port ?? null
  const offen = oeffnen.data

  return (
    <section className="card" aria-labelledby="tunnel-titel">
      <div className="card-head">
        <h2 className="card-title" id="tunnel-titel">
          Port-Tunnel
        </h2>
        <button type="button" className="btn btn-sm" onClick={onClose}>
          Schließen
        </button>
      </div>
      <div className="card-body stack" style={{ gap: 12 }}>
        {angebot.isPending ? (
          <span className="muted">Lädt …</span>
        ) : ports.length === 0 ? (
          <span className="muted">
            Für dieses Gerät ist kein Port als Tunnel freigegeben. Unter Verwaltung → Stammdaten lassen sich
            Tunnel-Ports eintragen; manche Vorlagen bringen welche mit (MikroTik: Winbox).
          </span>
        ) : offen ? (
          <>
            <p style={{ margin: 0 }}>
              {offen.port_name} zu <strong>{offen.device_name}</strong> ({offen.customer_name}) ist offen. Das Programm{' '}
              <span className="mono">netzwarte-tunnel</span> starten und den Code eingeben; danach das Programm mit der
              angezeigten lokalen Adresse verbinden.
            </p>
            <CopyField label="Tunnel-Code (nur jetzt sichtbar)" value={offen.code} />
            <CopyField label="Aufruf" value={`netzwarte-tunnel ${offen.server}`} />
            <span className="muted" style={{ fontSize: 12 }}>
              Der Tunnel endet mit Strg+C im Programm, unter Fernzugriff oder nach {Math.round(offen.expires_in_s / 60)}{' '}
              Minuten ohne Nutzung. Er steht im Protokoll.
            </span>
          </>
        ) : (
          <form
            className="row"
            style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}
            onSubmit={(e) => {
              e.preventDefault()
              if (gewaehlt !== null) oeffnen.mutate(gewaehlt)
            }}
          >
            <div className="field">
              <label htmlFor="tunnel-port">Port</label>
              <select
                id="tunnel-port"
                className="input"
                value={gewaehlt ?? ''}
                onChange={(e) => setPort(Number(e.target.value))}
              >
                {ports.map((p) => (
                  <option key={p.port} value={p.port}>
                    {p.name} (TCP {p.port})
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn btn-primary" disabled={oeffnen.isPending}>
              {oeffnen.isPending ? 'Öffnet …' : 'Tunnel öffnen'}
            </button>
          </form>
        )}
        {oeffnen.isError ? <span style={{ color: 'var(--crit)' }}>{(oeffnen.error as Error).message}</span> : null}
        {angebot.data && angebot.data.downloads.length > 0 ? (
          <div className="row" style={{ gap: 12, flexWrap: 'wrap', fontSize: 13 }}>
            <span className="muted">Programm herunterladen:</span>
            {angebot.data.downloads.map((name) => (
              <a key={name} href={`/api/tunnel/programm/${name}`} download>
                {PLATTFORMEN[name] ?? name}
              </a>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  )
}
